import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxcCostbasisV1RealizationQueryServiceRealizationGet } from '@interxfi/api-client/generated/hooks/ixc-costbasis-v1-realization-query-service-realization-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { buildBreadcrumbs } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcCostbasisV1RealizationState } from '@interxfi/types';
import {
  CostBasisRealizationEventDrawer,
  CostBasisRealizationEventSummary,
} from '../../../../../../../../../../../features/cost-basis/realization/cost-basis-realization-event';
import { getCostBasisRealizationStateListItems } from '../../../../../../../../../../../features/cost-basis/realization/cost-basis-realization-list-items';
import { getCostBasisRealizationPath } from '../../../../../../../../../../../features/cost-basis/realization/cost-basis-realization-routing';
import { CostBasisRealizationStatusPill } from '../../../../../../../../../../../features/cost-basis/realization/cost-basis-realization-status-pill';
import { CostBasisRealizationSummaryBar } from '../../../../../../../../../../../features/cost-basis/realization/cost-basis-realization-summary-bar';
import { AccountBreadcrumb } from '../../../../../../../../../../../features/registration/account/account-breadcrumb';
import { RegistrationAccountPageHeader } from '../../../../../../../../../../../features/registration/account/registration-account-page-header';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/registration/account/$accountId/_account/_cost-basis/asset/$assetId/realization/$realizationId',
)({
  loader: ({ params }) =>
    buildBreadcrumbs([
      {
        id: `account-${params.accountId}-asset-${params.assetId}-realization-list`,
        label: <AccountBreadcrumb accountId={params.accountId} displayIdOnly />,
        to: getCostBasisRealizationPath({
          kind: 'list',
          keys: { accountId: params.accountId, assetId: params.assetId },
        }),
      },
      {
        id: `account-${params.accountId}-asset-${params.assetId}-realization-${params.realizationId}`,
        label: 'Realization',
        to: getCostBasisRealizationPath({
          kind: 'detail',
          keys: { accountId: params.accountId, assetId: params.assetId, realizationId: params.realizationId },
        }),
        suffix: (
          <CopyButton
            content={params.realizationId}
            label="Copy Realization ID"
            tooltip={{ copy: params.realizationId, copied: 'Copied Realization ID' }}
          />
        ),
      },
    ]),
  component: AccountAssetRealizationDetail,
  head: () => getPageHead('Realization'),
});

function getListItems(data: IxcCostbasisV1RealizationState | undefined) {
  const items = getCostBasisRealizationStateListItems(data);

  return {
    general: {
      left: [items.statusPill, items.data.adjustedCost, items.data.realized, items.data.washedQuantity] as const satisfies ReadonlyListItem[],
      right: [
        items.realizationId,
        items.taxLotId,
        items.transactionId,
        items.assetId,
        items.data.holdingPeriodStartDate,
      ] as const satisfies ReadonlyListItem[],
    },
    closingTransaction: {
      left: [
        items.data.closingTransaction.totalQuantity,
        items.data.closingTransaction.applicableQuantity,
        items.data.closingTransaction.totalProceeds,
        items.data.closingTransaction.applicableProceeds,
      ] as const satisfies ReadonlyListItem[],
      right: [
        items.data.closingTransaction.tradeTimestamp,
        items.data.closingTransaction.tradeDate,
        items.data.closingTransaction.settleDate,
        ...match(items.data.closingTransaction.transactionType)
          .with({ portfolioTransaction: P.not(P.nullish) }, ({ portfolioTransaction }) => [portfolioTransaction.value.transactionId])
          .otherwise(() => []),
      ] as const satisfies ReadonlyListItem[],
    },
  };
}

function getMenuItems(accountId: string, realizationId: string) {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: CostBasisRealizationEventDrawer,
        props: { request: { accountId, realizationId } },
      },
    },
    actions: undefined,
  } as const satisfies EntityActionMenuConfig;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load realization', 'view this realization');

function AccountAssetRealizationDetail() {
  const { accountId, realizationId } = Route.useParams();
  const request = { accountId, realizationId } as const;
  const { data, error, isPending } = useIxcCostbasisV1RealizationQueryServiceRealizationGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.realization), [data?.realization]);
  const menuItems = useMemo(() => getMenuItems(accountId, realizationId), [accountId, realizationId]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <RegistrationAccountPageHeader
            accountId={accountId}
            actions={<EntityActionMenu {...menuItems} isPending={showPending} />}
            pills={<CostBasisRealizationStatusPill isPending={showPending} size="large" status={data?.realization?.status} />}
          />

          <CostBasisRealizationSummaryBar {...request} />

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <CostBasisRealizationEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Closing Transaction</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.closingTransaction.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.closingTransaction.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
